import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Filter as FilterListIcon, Flame, Sun, Snowflake, Phone as PhoneIcon, Search, Plus, X, Calendar, ChevronDown } from "lucide-react";
import { DownloadIcon, FilterIcon } from "../../../assets/icon";
import useCrmApi from "../../../components/hook/crm-req";
import useRequest from "../../../components/hook/use-req";
import { ContactModal } from "../../../components/crm/customer/update-customer";
import ReuseableDrawer from "../../../components/resuable-drawer";
import { TeamDetails } from "../../../components/crm/customer/types";
import Pagination from "../../../components/pagination";
import LeadFiltersPanel, { LeadFiltersState } from "./filters-panel";
import LeadDetailsModal from "./lead-details-modal";
import { AssignAgentModal } from "./assign-agent-modal";
import { NewTagModal } from "../../../components/crm/conversation/components/insight";
import {
  LeadRecord,
  goToLeadConversation,
  LeadTableRow,
  mapRecordToLead,
  recordToContactItem,
  updateLeadSafely,
} from "./shared";

// ─────────────────────────────────────────────────────────────
// conversation_agent's Leads page — same real /crm/unified data as the
// default Leads/All Leads pages, but as ONE page: stat tiles up top, then
// straight into the full real-paginated table. No "Immediate
// Recommendations" carousel, no 5-row "Recent Leads" preview-with-a-link —
// conversation_agent asked for the whole assigned list on one page instead
// of a teaser. See pages/leads/index.tsx for the role gate that picks this
// component. Deliberately a separate file rather than a shared-prop variant
// of LeadsPage/AllLeadsPage so neither of those (used by every other role)
// risk a regression from this change.
// ─────────────────────────────────────────────────────────────

type DashboardView = "realtime" | "fixed";

const StatTile: React.FC<{ icon: React.ReactNode; iconColor: string; label: string; value: number }> = ({
  icon, iconColor, label, value,
}) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex flex-col gap-3 min-w-0">
    <div className="flex items-center gap-2 min-w-0">
      <span className={`shrink-0 ${iconColor}`}>{icon}</span>
      <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight truncate">{label}</span>
    </div>
    <span className="text-2xl font-semibold text-indigo-600 dark:text-indigo-400 underline decoration-2 underline-offset-2">{value}</span>
  </div>
);

const ViewToggle: React.FC<{ value: DashboardView; onChange: (v: DashboardView) => void }> = ({ value, onChange }) => (
  <div className="flex items-center gap-2 shrink-0">
    {(["realtime", "fixed"] as DashboardView[]).map((v) => (
      <button
        key={v}
        onClick={() => onChange(v)}
        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
          value === v
            ? "bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900"
            : "bg-indigo-50 dark:bg-indigo-900/20 text-gray-500 dark:text-gray-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/40"
        }`}
      >
        {v === "realtime" ? "Real-time" : "Fixed Period"}
      </button>
    ))}
  </div>
);

const DateRangeFilter: React.FC<{ startDate: string; endDate: string; onApply: (s: string, e: string) => void }> = ({
  startDate, endDate, onApply,
}) => {
  const [open, setOpen] = useState(false);
  const [draftStart, setDraftStart] = useState(startDate);
  const [draftEnd, setDraftEnd] = useState(endDate);

  useEffect(() => {
    if (open) {
      setDraftStart(startDate);
      setDraftEnd(endDate);
    }
  }, [open, startDate, endDate]);

  const label = startDate && endDate ? `${startDate} – ${endDate}` : "All time";

  return (
    <div className="relative shrink-0">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-lg px-3.5 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900 whitespace-nowrap"
      >
        <Calendar size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
        {label}
        <ChevronDown size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-11 z-20 w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-4 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Start Date</label>
                <input
                  type="date"
                  value={draftStart}
                  onChange={(e) => setDraftStart(e.target.value)}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-indigo-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300">End Date</label>
                <input
                  type="date"
                  value={draftEnd}
                  onChange={(e) => setDraftEnd(e.target.value)}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-indigo-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { onApply(draftStart, draftEnd); setOpen(false); }}
                className="flex-1 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors"
              >
                Apply
              </button>
              <button
                onClick={() => { onApply("", ""); setOpen(false); }}
                className="flex-1 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const ConversationAgentLeads: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");

  const [view, setView] = useState<DashboardView>("fixed");
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });

  // Real backend-computed aggregates across ALL leads (not just the current
  // page) — same `summary` object the default Leads page reads. Contacted
  // and Converted have no equivalent field in that summary (real lead
  // `status` values seen from the backend never match "contact"/"convert"),
  // so those two tiles stay at 0 rather than showing a fabricated count.
  const [summary, setSummary] = useState({ total: 0, hot: 0, warm: 0, cold: 0 });

  const [optionLeads, setOptionLeads] = useState<LeadRecord[]>([]);
  const [tableLeads, setTableLeads] = useState<LeadRecord[]>([]);
  const [tableInitialLoading, setTableInitialLoading] = useState(true);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [editingLead, setEditingLead] = useState<LeadRecord | null>(null);
  const [detailsLead, setDetailsLead] = useState<LeadRecord | null>(null);
  const [detailsInitialTab, setDetailsInitialTab] = useState<"Details" | "Insight" | "Actions">("Actions");
  const [teamMembers, setTeamMembers] = useState<TeamDetails[]>([]);
  const [tagLead, setTagLead] = useState<LeadRecord | null>(null);
  const [assignLead, setAssignLead] = useState<LeadRecord | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<LeadFiltersState>({
    startDate: "", endDate: "", channel: "", tag: "", status: "",
  });

  const openLeadDetails = (lead: LeadRecord, tab: "Details" | "Insight" | "Actions" = "Actions") => {
    setDetailsLead(lead);
    setDetailsInitialTab(tab);
  };

  const handleTagsUpdated = (leadId: string, tags: string[]) => {
    setTableLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, tags } : l)));
  };

  const { makeRequest: getSummary } = useCrmApi("/crm/unified", "GET", { Authorization: `Bearer ${token}` });
  const { makeRequest: getOptionLeads } = useCrmApi("/crm/unified", "GET", { Authorization: `Bearer ${token}` });
  const { makeRequest: getTableLeads } = useCrmApi("/crm/unified", "GET", { Authorization: `Bearer ${token}` });
  const { makeRequest: getTeamList } = useRequest(`/profile/users`, "GET", { Authorization: `Bearer ${token}` });

  useEffect(() => {
    (async () => {
      const [response] = await getTeamList();
      if (Array.isArray(response)) setTeamMembers(response);
      else if (Array.isArray(response?.users)) setTeamMembers(response.users);
      else if (Array.isArray(response?.results)) setTeamMembers(response.results);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAssign = async (lead: LeadRecord, member: TeamDetails | null) => {
    const { ok, error } = await updateLeadSafely(lead.id, { custom: { assigned_to: member?.id ?? null } }, token);
    if (ok) {
      setTableLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, assignedTo: member?.id ?? null } : l)));
      toast.success(member ? `Assigned to ${member.first_name} ${member.last_name}` : "Unassigned");
    } else {
      toast.error(error || "Failed to update assignee");
    }
  };

  const handleSaveTagsForLead = async (tags: string[]): Promise<boolean> => {
    if (!tagLead) return false;
    const { ok, error } = await updateLeadSafely(tagLead.id, { tags }, token);
    if (ok) {
      handleTagsUpdated(tagLead.id, tags);
      toast.success("Tags saved");
      return true;
    }
    toast.error(error || "Failed to save tags");
    return false;
  };

  const loadSummary = async (range: { startDate: string; endDate: string } = dateRange) => {
    const params: Record<string, unknown> = { type: "lead", sort_order: "desc", page: 1, per_page: 1 };
    if (range.startDate) params.from_date = range.startDate;
    if (range.endDate) params.to_date = range.endDate;
    const [response, status] = await getSummary(undefined, params);
    if (status === 200 && response?.summary) {
      setSummary({
        total: response.summary.total_leads ?? 0,
        hot: response.summary.hot_leads ?? 0,
        warm: response.summary.warm_leads ?? 0,
        cold: response.summary.cold_leads ?? 0,
      });
    } else if (status === 200 && response) {
      // No `summary` object on this response — fall back to the real total
      // count at least, rather than showing every tile as 0.
      setSummary((s) => ({ ...s, total: response.total ?? response.counts?.leads ?? 0 }));
    }
  };

  useEffect(() => {
    (async () => {
      const [response, status] = await getOptionLeads(undefined, { type: "lead", sort_order: "desc", page: 1, per_page: 100 });
      if (status === 200 && response?.results) setOptionLeads(response.results.map(mapRecordToLead));
    })();
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyDateRange = (startDate: string, endDate: string) => {
    const range = { startDate, endDate };
    setDateRange(range);
    loadSummary(range);
  };

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchQuery(searchQuery), 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const fetchTableLeads = async (showPaginationLoader = false) => {
    try {
      if (showPaginationLoader) setIsPaginationLoading(true);
      else setTableInitialLoading(true);

      const params: Record<string, unknown> = {
        type: "lead", sort_order: "desc", page: currentPage, per_page: pageSize,
      };
      if (debouncedSearchQuery.trim()) params.search = debouncedSearchQuery.trim();
      if (appliedFilters.status) params.status = appliedFilters.status;
      if (appliedFilters.startDate) params.from_date = appliedFilters.startDate;
      if (appliedFilters.tag) params.tag = appliedFilters.tag;

      const [response] = await getTableLeads(undefined, params);
      if (response?.results && Array.isArray(response.results)) {
        let mapped: LeadRecord[] = response.results.map(mapRecordToLead);
        if (appliedFilters.channel) mapped = mapped.filter((l) => l.channel === appliedFilters.channel);
        if (appliedFilters.endDate) mapped = mapped.filter((l) => !l.updatedAt || l.updatedAt.slice(0, 10) <= appliedFilters.endDate);
        setTableLeads(mapped);
        setTotalPages(Math.max(1, Math.ceil((response.total || 0) / pageSize)));
      } else {
        setTableLeads([]);
        setTotalPages(1);
      }
    } catch {
      setTableLeads([]);
    } finally {
      setTableInitialLoading(false);
      setIsPaginationLoading(false);
    }
  };

  useEffect(() => {
    fetchTableLeads(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!tableInitialLoading) fetchTableLeads(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedSearchQuery, appliedFilters]);

  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, appliedFilters]);

  const channelOptions = useMemo(
    () => Array.from(new Set(optionLeads.map((l) => l.channel).filter(Boolean))) as string[],
    [optionLeads],
  );
  const tagOptions = useMemo(() => Array.from(new Set(optionLeads.flatMap((l) => l.tags))), [optionLeads]);
  const statusOptions = useMemo(
    () => Array.from(new Set(optionLeads.map((l) => l.status).filter(Boolean))) as string[],
    [optionLeads],
  );

  const activeFilterCount = [appliedFilters.startDate, appliedFilters.endDate, appliedFilters.channel, appliedFilters.tag, appliedFilters.status]
    .filter(Boolean).length;

  const clearFilterField = (field: keyof LeadFiltersState) => {
    setAppliedFilters((f) => ({ ...f, [field]: "" }));
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">Leads</h1>
          <div className="flex items-center gap-3">
            <ViewToggle value={view} onChange={setView} />
            <DateRangeFilter startDate={dateRange.startDate} endDate={dateRange.endDate} onApply={applyDateRange} />
          </div>
        </div>

        {view === "realtime" ? (
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-10 text-center text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">
            Real-time lead activity isn't available yet.
          </div>
        ) : (
          <>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Lead Performance</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Find, engage and convert your potential customers</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
              <StatTile icon={<FilterListIcon size={18} />} iconColor="text-gray-500 dark:text-gray-400" label="Total Leads" value={summary.total} />
              <StatTile icon={<Flame size={18} />} iconColor="text-red-500" label="Hot" value={summary.hot} />
              <StatTile icon={<Sun size={18} />} iconColor="text-amber-500" label="Warm" value={summary.warm} />
              <StatTile icon={<Snowflake size={18} />} iconColor="text-blue-500" label="Cold/ Nurture" value={summary.cold} />
              {/* No real backend field for these two — honest 0s rather than
                  fabricated numbers (see comment on `summary` state above). */}
              <StatTile icon={<PhoneIcon size={18} />} iconColor="text-emerald-500" label="Contacted" value={0} />
              <StatTile icon={<PhoneIcon size={18} />} iconColor="text-purple-500" label="Converted" value={0} />
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
              <div className="mb-3">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Leads Assigned</h3>
                <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Browse, filter, and reach out to leads assigned to you</p>
              </div>

              <div className="flex items-center gap-3 flex-wrap mb-3">
                <button
                  onClick={() => toast.info("Lead export is coming soon.")}
                  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
                >
                  Export <DownloadIcon />
                </button>
                <div className="relative flex-1 min-w-[200px]">
                  <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
                  <input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search leads by name, phone or email"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                  />
                </div>
                <button
                  onClick={() => setShowAddContact(true)}
                  className="flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 rounded-lg px-3.5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors whitespace-nowrap"
                >
                  <Plus size={15} /> Add New Contact
                </button>
                <button
                  onClick={() => setShowFilters(true)}
                  className="flex items-center gap-1.5 border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-lg px-3.5 py-2.5 text-sm font-medium hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors whitespace-nowrap"
                >
                  <FilterIcon /> Filter
                  {activeFilterCount > 0 && (
                    <span className="w-4 h-4 flex items-center justify-center rounded-full bg-indigo-600 text-white text-[10px]">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </div>

              {activeFilterCount > 0 && (
                <div className="flex items-center gap-2 flex-wrap mb-4">
                  {appliedFilters.status && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-full px-3 py-1">
                      Status: {appliedFilters.status}
                      <button onClick={() => clearFilterField("status")} className="bg-transparent border-none cursor-pointer text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 p-0 flex items-center"><X size={12} /></button>
                    </span>
                  )}
                  {appliedFilters.tag && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-full px-3 py-1">
                      Tags: {appliedFilters.tag}
                      <button onClick={() => clearFilterField("tag")} className="bg-transparent border-none cursor-pointer text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 p-0 flex items-center"><X size={12} /></button>
                    </span>
                  )}
                  {appliedFilters.channel && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-full px-3 py-1">
                      Channel: {appliedFilters.channel}
                      <button onClick={() => clearFilterField("channel")} className="bg-transparent border-none cursor-pointer text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 p-0 flex items-center"><X size={12} /></button>
                    </span>
                  )}
                  {(appliedFilters.startDate || appliedFilters.endDate) && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-full px-3 py-1">
                      Date: {appliedFilters.startDate || "…"} – {appliedFilters.endDate || "…"}
                      <button
                        onClick={() => setAppliedFilters((f) => ({ ...f, startDate: "", endDate: "" }))}
                        className="bg-transparent border-none cursor-pointer text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 p-0 flex items-center"
                      ><X size={12} /></button>
                    </span>
                  )}
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-800">
                      <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Contact Name</th>
                      <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Phone</th>
                      <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Source</th>
                      <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Tags</th>
                      <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Status</th>
                      <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableInitialLoading || isPaginationLoading ? (
                      <tr>
                        <td colSpan={7} className="text-center py-10">
                          <div className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600" />
                            <span className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Loading…</span>
                          </div>
                        </td>
                      </tr>
                    ) : tableLeads.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-10 text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">
                          {activeFilterCount > 0 || debouncedSearchQuery ? "No leads match your filters." : "No leads assigned to you yet."}
                        </td>
                      </tr>
                    ) : (
                      tableLeads.map((lead) => (
                        <LeadTableRow
                          key={lead.id}
                          lead={lead}
                          onView={() => goToLeadConversation(navigate, lead)}
                          onEdit={() => setEditingLead(lead)}
                          onOpenDetails={(tab) => openLeadDetails(lead, tab)}
                          onManageTags={setTagLead}
                          teamMembers={teamMembers}
                          onOpenAssign={setAssignLead}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="pt-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  isLoading={isPaginationLoading}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
                />
              </div>
            </div>
          </>
        )}
      </div>

      <LeadFiltersPanel
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={appliedFilters}
        channelOptions={channelOptions}
        tagOptions={tagOptions}
        statusOptions={statusOptions}
        onApply={setAppliedFilters}
        onReset={() => setAppliedFilters({ startDate: "", endDate: "", channel: "", tag: "", status: "" })}
      />

      <ReuseableDrawer isOpen={showAddContact} onClose={() => setShowAddContact(false)} title="Add New Lead" initialWidth="30%">
        <ContactModal
          mode="lead"
          variant="drawer"
          isOpen={showAddContact}
          onClose={() => setShowAddContact(false)}
          onCreated={() => { setShowAddContact(false); fetchTableLeads(true); loadSummary(); }}
        />
      </ReuseableDrawer>

      <ReuseableDrawer isOpen={!!editingLead} onClose={() => setEditingLead(null)} title="Update Lead" initialWidth="30%">
        <ContactModal
          mode="lead"
          variant="drawer"
          isOpen={!!editingLead}
          onClose={() => setEditingLead(null)}
          contact={editingLead ? recordToContactItem(editingLead) : null}
          onUpdated={() => { setEditingLead(null); fetchTableLeads(true); }}
        />
      </ReuseableDrawer>

      <LeadDetailsModal
        lead={detailsLead}
        initialTab={detailsInitialTab}
        onClose={() => setDetailsLead(null)}
        onTagsUpdated={handleTagsUpdated}
        teamMembers={teamMembers}
      />

      <NewTagModal
        isOpen={!!tagLead}
        onClose={() => setTagLead(null)}
        existingTags={tagLead?.tags || []}
        onSave={handleSaveTagsForLead}
      />

      <AssignAgentModal
        lead={assignLead}
        teamMembers={teamMembers}
        onClose={() => setAssignLead(null)}
        onAssign={handleAssign}
      />
    </div>
  );
};

export default ConversationAgentLeads;
